/**
 * Theme utility functions for WERESP_WEB
 * Handles theme-based styling for light, dark, and dark-monochrome themes
 */

/**
 * Get background color class based on theme
 * @param {string} theme - Current theme ('light', 'dark', 'dark-monochrome', 'darker-monochrome')
 * @param {string} component - Component type for specific styling
 * @returns {string} Tailwind CSS class
 */
export const getBackgroundClass = (theme, component = 'default') => {
  switch (theme) {
    case 'light':
      return 'bg-white'
    case 'dark':
      return 'bg-mid-blue'
    case 'dark-monochrome':
      return 'bg-dark-grey'
    case 'darker-monochrome':
      return 'bg-darker-grey'
    default:
      return 'bg-mid-blue'
  }
}

/**
 * Get primary color class based on theme
 * @param {string} theme - Current theme ('light', 'dark', 'dark-monochrome', 'darker-monochrome')
 * @param {string} component - Component type for specific styling
 * @returns {string} Tailwind CSS class
 */
export const getPrimaryClass = (theme, component = 'default') => {
  switch (theme) {
    case 'light':
      return 'bg-sky-blue'
    case 'dark':
      return 'bg-blue'
    case 'dark-monochrome':
      return 'bg-medium-grey'
    case 'darker-monochrome':
      return 'bg-darkest-grey'
    default:
      return 'bg-blue'
  }
}

/**
 * Get text color class based on theme
 * @param {string} theme - Current theme ('light', 'dark', 'dark-monochrome', 'darker-monochrome')
 * @returns {string} Tailwind CSS class
 */
export const getTextClass = (theme) => {
  switch (theme) {
    case 'light':
      return 'text-mid-blue'
    case 'dark':
    case 'dark-monochrome':
    case 'darker-monochrome':
      return 'text-white'
    default:
      return 'text-white'
  }
}

/**
 * Get border color class based on theme
 * @param {string} theme - Current theme ('light', 'dark', 'dark-monochrome', 'darker-monochrome')
 * @returns {string} Tailwind CSS class
 */
export const getBorderClass = (theme) => {
  switch (theme) {
    case 'light':
      return 'border-sky-blue'
    case 'dark':
      return 'border-blue'
    case 'dark-monochrome':
      return 'border-light-grey'
    case 'darker-monochrome':
      return 'border-medium-grey'
    default:
      return 'border-blue'
  }
}

/**
 * Get hover color class based on theme
 * @param {string} theme - Current theme ('light', 'dark', 'dark-monochrome', 'darker-monochrome')
 * @returns {string} Tailwind CSS class
 */
export const getHoverClass = (theme) => {
  switch (theme) {
    case 'light':
      return 'hover:bg-sky-blue-hover'
    case 'dark':
      return 'hover:bg-blue-hover'
    case 'dark-monochrome':
      return 'hover:bg-dark-grey-hover'
    case 'darker-monochrome':
      return 'hover:bg-darker-grey-hover'
    default:
      return 'hover:bg-blue-hover'
  }
}

/**
 * Get button styling classes based on theme
 * @param {string} theme - Current theme ('light', 'dark', 'dark-monochrome')
 * @param {boolean} isDisabled - Whether the button is disabled
 * @returns {object} Object with base and hover classes
 */
export const getButtonClasses = (theme, isDisabled = false) => {
  const baseClass = getPrimaryClass(theme)
  const hoverClass = isDisabled ? '' : getHoverClass(theme)

  return {
    base: baseClass,
    hover: hoverClass,
    disabled: isDisabled ? 'opacity-50 cursor-not-allowed' : '',
  }
}

/**
 * Get main background color for the application
 * @param {string} theme - Current theme ('light', 'dark', 'dark-monochrome', 'darker-monochrome')
 * @returns {string} Tailwind CSS class
 */
export const getMainBackgroundClass = (theme) => {
  switch (theme) {
    case 'light':
      return 'bg-light-blue'
    case 'dark':
      return 'bg-dark-blue'
    case 'dark-monochrome':
      return 'bg-dark-grey'
    case 'darker-monochrome':
      return 'bg-darker-grey'
    default:
      return 'bg-dark-blue'
  }
}

/**
 * Get chatbot background color class based on theme and mode
 * @param {string} theme - Current theme ('light', 'dark', 'dark-monochrome', 'darker-monochrome')
 * @param {string} mode - Chatbot mode ('ask' or 'agent')
 * @returns {string} Tailwind CSS class
 */
export const getChatbotBackgroundClass = (theme, mode) => {
  if (mode === 'ask') {
    // Blue background for ask mode
    switch (theme) {
      case 'light':
        return 'bg-sky-blue'
      case 'dark':
        return 'bg-blue'
      case 'dark-monochrome':
        return 'bg-medium-grey'
      case 'darker-monochrome':
        return 'bg-darkest-grey'
      default:
        return 'bg-blue'
    }
  } else {
    // Agent mode: a shade of the theme's own palette (navy in dark, black in
    // the monochrome themes), pale in light so the theme's dark text stays
    // readable on it. Grey in the blue dark theme looked out of place.
    switch (theme) {
      case 'light':
        return 'bg-light-blue'
      case 'dark':
        return 'bg-dark-blue'
      case 'dark-monochrome':
        return 'bg-black'
      case 'darker-monochrome':
        return 'bg-black'
      default:
        return 'bg-dark-blue'
    }
  }
}

/**
 * Check if theme is a dark variant
 * @param {string} theme - Current theme ('light', 'dark', 'dark-monochrome', 'darker-monochrome')
 * @returns {boolean}
 */
const isDarkTheme = (theme) => {
  return (
    theme === 'dark' ||
    theme === 'dark-monochrome' ||
    theme === 'darker-monochrome'
  )
}

/**
 * Get input/select/dropdown theme classes
 * @param {string} theme - Current theme
 * @returns {string} Tailwind classes for form inputs
 */
export const getInputThemeClasses = (theme) => {
  switch (theme) {
    case 'light':
      return 'bg-white border-sky-blue text-mid-blue focus:ring-2 focus:ring-sky-blue focus:border-sky-blue'
    case 'dark':
      return 'bg-mid-blue border-blue text-white focus:ring-2 focus:ring-blue focus:border-blue'
    case 'dark-monochrome':
    case 'darker-monochrome':
      return 'bg-dark-grey-hover border-light-grey text-white focus:ring-2 focus:ring-medium-grey focus:border-light-grey'
    default:
      return 'bg-mid-blue border-blue text-white focus:ring-2 focus:ring-blue focus:border-blue'
  }
}

/**
 * Get secondary/panel background (slightly lighter/darker than main)
 * @param {string} theme - Current theme
 * @returns {string} Tailwind class
 */
export const getSecondaryBackgroundClass = (theme) => {
  switch (theme) {
    case 'light':
      return 'bg-light-blue'
    case 'dark':
      return 'bg-mid-blue'
    case 'dark-monochrome':
      return 'bg-dark-grey-hover'
    case 'darker-monochrome':
      return 'bg-darker-grey-hover'
    default:
      return 'bg-mid-blue'
  }
}

/**
 * Get button/action theme classes (secondary buttons)
 * @param {string} theme - Current theme
 * @returns {string} Tailwind classes for buttons
 */
const getSecondaryButtonClasses = (theme) => {
  switch (theme) {
    case 'light':
      return 'bg-light-blue text-mid-blue hover:bg-sky-blue border-sky-blue focus:ring-sky-blue focus:ring-offset-light-blue'
    case 'dark':
      return 'bg-mid-blue text-white hover:bg-blue-hover border-blue focus:ring-blue focus:ring-offset-dark-blue'
    case 'dark-monochrome':
    case 'darker-monochrome':
      return 'bg-dark-grey-hover text-white hover:bg-medium-grey border-light-grey focus:ring-medium-grey focus:ring-offset-dark-grey'
    default:
      return 'bg-mid-blue text-white hover:bg-blue-hover border-blue'
  }
}

/**
 * Get muted/subtle text color for labels and secondary info
 * @param {string} theme - Current theme
 * @returns {string} Tailwind class
 */
export const getMutedTextClass = (theme) => {
  switch (theme) {
    case 'light':
      return 'text-gray-600'
    case 'dark':
    case 'dark-monochrome':
    case 'darker-monochrome':
      return 'text-gray-400'
    default:
      return 'text-gray-400'
  }
}
